import type { User } from "firebase/auth";
import { useEffect, useMemo, useRef, useState } from "react";
import type * as api from "./api";
import { readingTime } from "./format";
import { go, useTitle } from "./hooks";
import { useMedia } from "./Menu";
import { INSTALLED, showSyncBadge, TOUCH } from "./syncBadge";
import { userScope } from "./offline/db";
import { CHANGE_LABEL, canChange } from "./offline/messages";
import { dayKeys } from "./offline/cache";
import { entryStop } from "./offline/local";
import { localNow } from "./visits";
import { discardText, doneElsewhere, needsServerDay, OTHER_PICKUP_NOTE, otherPickupKept, syncChip, syncFixShown, syncRowText, syncStopLabel } from "./visitFix";
import { useOnline } from "./offline/day";
import { syncDayLoader } from "./offline/pages";
import { badgeCount, badgeText, discard, lastSent, type OutboxEntry, waiting } from "./offline/outbox";
import { kick, useOutbox } from "./offline/useOutbox";
import { PhotoThumb } from "./offline/PhotoThumb";
import { Skeleton } from "./Skeleton";

const KIND_TEXT: Record<OutboxEntry["kind"], string> = { start: "Pre-trip check", load: "Load", pickup: "Pickup", visit: "Visit", stop: "Stop", finish: "End odometer", photo: "Photo" };

/** Today can change the entry: not a visit, load or photo, and a change can help (SO-1061). */
const fixedOnToday = (entry: OutboxEntry) => entry.kind !== "visit" && entry.kind !== "load" && entry.kind !== "photo" && canChange(entry.reason);

/** WF-25: the entries on this phone, their state, Send now, and Discard for a Not saved entry. */
export function SyncPage({ slug, base, org, user, canView }: { slug: string; base: string; org: api.Bootstrap; user: User; canView: boolean | undefined }) {
  useTitle(`Sync · ${org.name}`);
  const scope = useMemo(() => userScope(slug, user.uid), [slug, user.uid]);
  const { all, signIn } = useOutbox(scope);
  const online = useOnline();
  const [note, setNote] = useState<string | null>(null);
  const [day, setDay] = useState<api.RouteDay | null>(null);
  const [dayDone, setDayDone] = useState(false);
  const fromServer = useRef(false);
  const today = localNow(new Date(), org.time_zone).day;
  const loadDay = useMemo(() => syncDayLoader(scope, () => user.getIdToken()), [scope, user]);
  const rows = (all ?? []).filter((entry) => needsServerDay([entry])).length;
  const dayWait = online && rows > 0 && !dayDone;
  useEffect(() => {
    let live = true;
    // The day that Today keeps on the phone tells which stops have a result on the server.
    void scope
      .read<api.RouteDay>(dayKeys.route(today))
      .then((kept) => {
        if (live && !fromServer.current) setDay(kept?.value ?? null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [scope, today, all]);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!dayWait) return;
    // One load for each page load; a second call gets the same promise (SO-1116).
    void loadDay(today, rows).then((found) => {
      if (!mounted.current) return;
      if (found) {
        fromServer.current = found.at === null;
        setDay(found.value);
      }
      setDayDone(true);
    });
  }, [dayWait, loadDay, today, rows]);
  const stopOf = (entry: OutboxEntry) => {
    const id = entry.kind === "stop" ? entryStop(entry) : undefined;
    return id ? day?.routes.flatMap((route) => route.stops).find((stop) => stop.id === id) : undefined;
  };
  const told = useRef(new Set<string>());
  for (const entry of all ?? []) {
    const stop = stopOf(entry);
    if (!doneElsewhere(entry, stop) || told.current.has(entry.id)) continue;
    told.current.add(entry.id);
    console.warn(`sync: refused first entry ${entry.id} is not needed, its stop is ${stop?.state} on the server; the row asks for Discard`);
  }
  if (canView === false) {
    return (
      <main data-testid="sync-hidden">
        <h1 tabIndex={-1}>Sync</h1>
        <p>Your role does not record entries on a phone.</p>
      </main>
    );
  }
  const list = all ?? [];
  const count = list.filter(waiting).length;
  const failed = list.filter((entry) => entry.state === "failed").length;
  const last = lastSent(list);
  const drop = async (entry: OutboxEntry) => {
    if (!confirm(discardText(entry, stopOf(entry)))) return;
    setNote((await discard(scope, entry.id)) ? `Discarded: ${entry.label}` : null);
  };
  return (
    <main className="sync-page" data-testid="sync-page" data-waiting={count} data-failed={failed}>
      <h1 tabIndex={-1}>Sync</h1>
      <p className="sync-summary" data-testid="sync-summary">
        <span data-testid="sync-waiting">{count} waiting</span>
        {failed > 0 && <span className="error"> · {failed} not saved</span>}
        <span> · last send: {last === null ? "none" : readingTime(new Date(last).toISOString(), org.time_zone)}</span>
        <span> · {online ? "online" : "offline"}</span>{" "}
        <button type="button" className="primary" data-testid="sync-send" disabled={count === 0} onClick={() => kick(scope, true, "button")}>
          Send now
        </button>
      </p>
      {signIn && (
        <p role="alert" className="error" data-testid="sync-sign-in">
          Sign in again to send the entries. They stay on this phone.
        </p>
      )}
      <p role="status" data-testid="sync-note">
        {note}
      </p>
      {all === null ? (
        <Skeleton rows={3} />
      ) : list.length === 0 ? (
        <p data-testid="sync-empty">No entries on this phone. Entries and photos from Route and Record a visit show here until the server has them.</p>
      ) : (
        <table className="list sync-list" data-testid="sync-list">
          <thead>
            <tr>
              <th scope="col">Entry</th>
              <th scope="col">Made</th>
              <th scope="col">State</th>
              <th scope="col">Tries</th>
              <th scope="col">
                <span className="visually-hidden">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {[...list].reverse().map((entry) => (
              <tr key={entry.id} data-testid="sync-entry" data-id={entry.id} data-kind={entry.kind} data-state={entry.state}>
                <th scope="row" className="wrap">
                  {entry.kind === "photo" && <PhotoThumb scope={scope} id={entry.id} alt="" />}
                  <span className="sub">{KIND_TEXT[entry.kind]}</span> {syncStopLabel(entry, list, stopOf(entry))}
                </th>
                <td>{readingTime(new Date(entry.created_at).toISOString(), org.time_zone)}</td>
                <td className="wrap">
                  <span className={`sync-state ${syncChip(entry).tone}`} data-testid="sync-state">
                    {syncChip(entry).text}
                  </span>
                  {entry.reason && entry.state !== "sent" && (
                    <span className={entry.state === "failed" ? "error" : "sub"} data-testid="sync-reason">
                      {" "}
                      {entry.state === "failed" ? syncRowText(entry, stopOf(entry), fixedOnToday(entry), dayWait) : entry.reason}
                    </span>
                  )}
                  {entry.state === "sent" && otherPickupKept(entry) && (
                    <span className="error" data-testid="sync-other-pickup">
                      {" "}
                      {OTHER_PICKUP_NOTE}
                    </span>
                  )}
                  {entry.state === "failed" && entry.detail && (
                    <span className="sub sync-detail" data-testid="sync-detail">
                      {" "}
                      Server text, for the office: {entry.detail}
                    </span>
                  )}
                </td>
                <td data-label="Tries">{entry.tries}</td>
                <td className="row-actions">
                  {entry.state === "failed" && (
                    <>
                      {entry.kind === "photo" && <PhotoThumb scope={scope} id={entry.id} alt="" copy />}{" "}
                      {syncFixShown(entry, stopOf(entry), fixedOnToday(entry), dayWait) && (
                        <a href={`${base}/today`} onClick={go}>
                          Fix on Route
                        </a>
                      )}
                      {entry.kind === "visit" && canChange(entry.reason) && (
                        <a href={`${base}/visit?fix=${encodeURIComponent(entry.id)}`} onClick={go} data-testid="sync-fix">
                          {CHANGE_LABEL}
                        </a>
                      )}{" "}
                      <button type="button" className="danger" data-testid="sync-discard" onClick={() => void drop(entry)}>
                        Discard
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}

/** The header badge: the entries that the server does not have yet (U-18). */
export function SyncBadge({ slug, base, uid }: { slug: string; base: string; uid: string }) {
  const scope = useMemo(() => userScope(slug, uid), [slug, uid]);
  const { all } = useOutbox(scope);
  const count = badgeCount(all ?? []);
  const bad = (all ?? []).some((entry) => entry.state === "failed");
  const touch = useMedia(TOUCH);
  const installed = useMedia(INSTALLED);
  if (!showSyncBadge(count, bad, touch || installed)) return null;
  return (
    <a
      className={`sync-badge${bad ? " bad" : ""}`}
      href={`${base}/sync`}
      onClick={go}
      data-testid="sync-badge"
      aria-label={`Sync: ${badgeText(count, bad)}`}
      title={badgeText(count, bad)}
    >
      {count}
      <span className="sync-badge-word" aria-hidden="true">
        &nbsp;not sent
      </span>
    </a>
  );
}
